import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  Tooltip,
  FormHelperText,
  Checkbox,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Delete,
  CalendarToday,
  AccessTime,
  LocationOn,
  AttachMoney,
  Refresh,
  Close,
  ExpandMore,
  EventNote,
  CheckCircle,
  People as PeopleIcon
} from '@mui/icons-material';
import { slotsAPI, contactsAPI, bookingTemplatesAPI } from '../services/api';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, isValid, parse } from 'date-fns';

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Enum mappings from API
const STATUS_ENUM = {
  0: 'AVAILABLE',
  1: 'BOOKED',
  2: 'BLOCKED',
  3: 'CONFIRMED',

  // Support string values from API
  'AVAILABLE': 'AVAILABLE',
  'BOOKED': 'BOOKED',
  'BLOCKED': 'BLOCKED',
  'CONFIRMED': 'CONFIRMED',
  'FULL': 'FULL',
  'GENERATED': 'AVAILABLE' // Map GENERATED to AVAILABLE
};

const bookingTypes = ['ONLINE', 'OFFLINE', 'BOTH'];
const MAX_SLOT_TITLE_LENGTH = 50;
const MAX_SLOT_DESCRIPTION_LENGTH = 250;
const MAX_SLOT_PRICE = 999999;

const limitSlotPriceInput = (value) => {
  if (value === '') return value;
  const price = Number(value);
  return Number.isFinite(price) && price > MAX_SLOT_PRICE ? String(MAX_SLOT_PRICE) : value;
};

const parseSlotPrice = (value) =>
  Math.min(MAX_SLOT_PRICE, Math.max(0, Number.parseFloat(value) || 0));

/** GET /customer/booking-templates/template/:contactId — unwrap template object */
function unwrapTemplateFromByContactResponse(response) {
  const raw = response?.data;
  const data = raw?.data ?? raw?.payload ?? raw;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data;
}

function normalizeTemplateBookingType(bt) {
  if (bt == null || bt === '') return undefined;
  const s = String(bt).toUpperCase();
  if (bookingTypes.includes(s)) return s;
  const n = Number(bt);
  if (n === 0) return 'ONLINE';
  if (n === 1) return 'OFFLINE';
  if (n === 2) return 'BOTH';
  return undefined;
}

function templateDisplayName(tmpl) {
  if (!tmpl || typeof tmpl !== 'object') return '';
  return String(tmpl.name ?? tmpl.templateName ?? tmpl.title ?? '').trim();
}

/** Label for template dropdown rows */
function templateOptionLabel(t) {
  if (!t || typeof t !== 'object') return '';
  return String(t.name ?? t.templateName ?? t.title ?? '').trim() || `ID ${t.id ?? ''}`;
}

function getTemplateContactId(template) {
  if (!template || typeof template !== 'object') return null;
  return (
    template.contactId ??
    template.contact_id ??
    (template.contact && typeof template.contact === 'object' ? template.contact.id : undefined)
  );
}

function isTemplateAssignedToContact(template, contactId) {
  const assigned = getTemplateContactId(template);
  if (assigned == null || assigned === '') return false;
  return String(assigned) === String(contactId);
}

/** Templates linked to this contact in GET /booking-templates list */
function templatesForContactSelection(allTemplates, contactId) {
  if (contactId === '' || contactId == null) return [];
  return (allTemplates || []).filter((template) => isTemplateAssignedToContact(template, contactId));
}

/** Keep selected template visible if it came from GET-by-contact but list rows omit contactId */
function mergeSelectedTemplateOption(options, templateId, templateName, allTemplates) {
  if (!templateId && templateId !== 0) return options;
  const idStr = String(templateId);
  if (options.some((t) => String(t.id) === idStr)) return options;
  const found = (allTemplates || []).find((t) => String(t.id) === idStr);
  if (found) return [...options, found];
  const label = String(templateName || '').trim();
  return [...options, { id: templateId, name: label || idStr }];
}

/** POST /api/customer/generate-slots/add-slot — times as "HH:mm" per Swagger */
function normalizeSlotTime(t) {
  if (t == null || t === '') return '';
  const s = String(t).trim();
  const parts = s.split(':');
  if (parts.length < 2) return s;
  const h = String(parts[0]).padStart(2, '0');
  const m = String(parts[1]).padStart(2, '0');
  return `${h}:${m}`;
}

/** Minutes between two "HH:mm" times (same day); 0 if invalid or end <= start. */
function minutesBetween(startHHmm, endHHmm) {
  if (!startHHmm || !endHHmm) return 0;
  const [sh, sm] = String(startHHmm).split(':').map((x) => Number.parseInt(x, 10));
  const [eh, em] = String(endHHmm).split(':').map((x) => Number.parseInt(x, 10));
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 0;
  return Math.max(0, eh * 60 + em - (sh * 60 + sm));
}

function formatSlotTime(timeString) {
  if (!timeString) return 'N/A';
  return timeString.substring(0, 5);
}

function getStatusLabelFromEnum(status) {
  const label = STATUS_ENUM[status] || 'UNKNOWN';
  return label === 'BOOKED' ? 'FULL' : label;
}

function getSlotStatusChipStyles(slotStatus) {
  const status = STATUS_ENUM[slotStatus];
  if (status === 'AVAILABLE') {
    return { bgcolor: 'rgba(76, 175, 80, 0.12)', color: '#43a047' };
  }
  if (status === 'BOOKED' || status === 'FULL') {
    return { bgcolor: 'rgba(244, 67, 54, 0.12)', color: '#e57373' };
  }
  return { bgcolor: 'rgba(0, 0, 0, 0.04)', color: '#546e7a' };
}

function isSlotDeleteDisabled(slot) {
  return slot.status === 1 || STATUS_ENUM[slot.status] === 'BOOKED' || STATUS_ENUM[slot.status] === 'FULL';
}

function formatTablePaginationLabel({ from, to, count }) {
  const total = count === -1 ? `more than ${to}` : String(count);
  return `${from}-${to} of ${total}`;
}

function SlotTableRow({ slot, contactDisplayName, onDelete }) {
  const chipStyles = getSlotStatusChipStyles(slot.status);
  return (
    <TableRow
      hover
      sx={{
        bgcolor: '#fff',
        '&:hover': { bgcolor: '#f8f9ff !important' },
        transition: 'background-color 0.2s',
      }}
    >
      <TableCell sx={{ color: '#546e7a' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AccessTime fontSize="small" sx={{ color: '#7986cb' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#5c6bc0' }}>
            {formatSlotTime(slot.startTime)} - {formatSlotTime(slot.endTime)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ color: '#546e7a' }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#455a64' }}>
          {slot.title || 'N/A'}
        </Typography>
        {slot.description && (
          <Typography variant="caption" display="block" noWrap sx={{ maxWidth: 150, color: '#90a4ae' }}>
            {slot.description}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ color: '#546e7a' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon fontSize="small" sx={{ color: '#b0bec5' }} />
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#546e7a' }}>
            {contactDisplayName}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ color: '#90a4ae' }}>
        <Typography variant="body2">
          {slot.templateName || '---'}
        </Typography>
      </TableCell>
      <TableCell sx={{ color: '#546e7a' }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {slot.duration || '0'} min
        </Typography>
      </TableCell>
      <TableCell sx={{ color: '#546e7a' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#43a047' }}>
          {slot.price ? `€${slot.price}` : 'Free'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={getStatusLabelFromEnum(slot.status)}
          size="small"
          sx={{
            borderRadius: '6px',
            fontWeight: 600,
            bgcolor: chipStyles.bgcolor,
            color: chipStyles.color,
          }}
        />
      </TableCell>
      <TableCell align="center" sx={{ color: '#546e7a' }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <IconButton
            size="small"
            onClick={() => onDelete(slot)}
            disabled={isSlotDeleteDisabled(slot)}
            sx={{
              color: '#e57373',
              bgcolor: 'rgba(244, 67, 54, 0.06)',
              '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.12)' },
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function slotsApiErrorMessage(data) {
  if (!data) return 'Unknown error';
  if (typeof data === 'string') return data;
  if (typeof data.error === 'string') return data.error;
  if (data.error?.message) return data.error.message;
  if (data.message) return data.message;
  if (data.data?.error?.message) return data.data.error.message;
  return 'Unknown error';
}

const Slots = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // State management
  const [groupedSlots, setGroupedSlots] = useState({});
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [statistics, setStatistics] = useState({
    totalSlots: 0,
    availableSlots: 0,
    bookedSlots: 0,
    todaysSlots: 0
  });

  // Dialog states
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    contactId: '',
    templateId: '',
    startDate: '',
    endDate: '',
    status: '',
    bookingType: ''
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Generate form data
  const [generateFormData, setGenerateFormData] = useState({
    contactId: '',
    startDate: '',
    endDate: '',
    startTime: '09:00',
    endTime: '17:00',
    duration: 60,
    daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    templateId: '',
    templateName: '',
    title: '',
    description: '',
    price: '',
    bookingType: 'ONLINE',
    isRecurring: false,
    recurringGroupId: null,
    source: 'CUSTOM',
    excludeHolidays: true,
    maxSlotsPerDay: 8
  });

  // Add form data (single slot)
  const [addFormData, setAddFormData] = useState({
    contactId: '',
    date: '',
    startTime: '10:00',
    endTime: '10:40',
    duration: 40,
    maxCapacity: 1,
    templateId: '',
    templateName: '',
    title: '',
    description: '',
    price: '',
    approvalType: 'AUTO'
  });

  // Edit form data (single slot: contactId, date, startTime, endTime, duration)
  const [editFormData, setEditFormData] = useState({
    contactId: '',
    date: '',
    startTime: '',
    endTime: '',
    duration: 60,
    maxCapacity: 1,
    templateId: '',
    templateName: '',
    title: '',
    description: '',
    price: '',
    approvalType: 'AUTO'
  });

  const generateContactTemplateSeq = useRef(0);
  const addContactTemplateSeq = useRef(0);
  const editContactTemplateSeq = useRef(0);

  /** When contact changes: GET template by contactId and fill title (and related fields). */
  const applyContactTemplateToGenerateForm = async (contactId) => {
    const seq = ++generateContactTemplateSeq.current;
    if (!contactId) {
      setGenerateFormData((prev) => ({
        ...prev,
        contactId: '',
        templateId: '',
        templateName: '',
        title: '',
      }));
      return;
    }
    setGenerateFormData((prev) => ({ ...prev, contactId }));
    try {
      const res = await bookingTemplatesAPI.getTemplateByContactId(contactId);
      if (seq !== generateContactTemplateSeq.current) return;
      const tmpl = unwrapTemplateFromByContactResponse(res);
      if (!tmpl) {
        setGenerateFormData((prev) => ({
          ...prev,
          contactId,
          templateId: '',
          templateName: '',
          title: '',
        }));
        return;
      }
      const name = templateDisplayName(tmpl);
      const tid = tmpl.id ?? tmpl.templateId;
      const bt = normalizeTemplateBookingType(tmpl.bookingType);
      setGenerateFormData((prev) => ({
        ...prev,
        contactId,
        templateId: tid != null && tid !== '' ? String(tid) : '',
        templateName: name,
        title: (name || prev.title).slice(0, MAX_SLOT_TITLE_LENGTH),
        description: String(tmpl.description != null ? tmpl.description : prev.description).slice(0, MAX_SLOT_DESCRIPTION_LENGTH),
        duration: tmpl.duration != null ? tmpl.duration : prev.duration,
        price: limitSlotPriceInput(tmpl.price != null ? String(tmpl.price) : prev.price),
        bookingType: bt || prev.bookingType,
      }));
    } catch {
      if (seq !== generateContactTemplateSeq.current) return;
      setGenerateFormData((prev) => ({
        ...prev,
        contactId,
        templateId: '',
        templateName: '',
        title: '',
      }));
    }
  };

  const applyContactTemplateToAddForm = async (contactId) => {
    const seq = ++addContactTemplateSeq.current;
    if (!contactId) {
      setAddFormData((prev) => ({
        ...prev,
        contactId: '',
        templateId: '',
        templateName: '',
        title: '',
      }));
      return;
    }
    setAddFormData((prev) => ({ ...prev, contactId }));
    try {
      const res = await bookingTemplatesAPI.getTemplateByContactId(contactId);
      if (seq !== addContactTemplateSeq.current) return;
      const tmpl = unwrapTemplateFromByContactResponse(res);
      if (!tmpl) {
        setAddFormData((prev) => ({
          ...prev,
          contactId,
          templateId: '',
          templateName: '',
          title: '',
        }));
        return;
      }
      const name = templateDisplayName(tmpl);
      const tid = tmpl.id ?? tmpl.templateId;
      setAddFormData((prev) => ({
        ...prev,
        contactId,
        templateId: tid != null && tid !== '' ? String(tid) : '',
        templateName: name,
        title: (name || prev.title).slice(0, MAX_SLOT_TITLE_LENGTH),
        description: String(tmpl.description != null ? tmpl.description : prev.description).slice(0, MAX_SLOT_DESCRIPTION_LENGTH),
        price: limitSlotPriceInput(tmpl.price != null ? String(tmpl.price) : prev.price),
      }));
    } catch {
      if (seq !== addContactTemplateSeq.current) return;
      setAddFormData((prev) => ({
        ...prev,
        contactId,
        templateId: '',
        templateName: '',
        title: '',
      }));
    }
  };

  const applyContactTemplateToEditForm = async (contactId) => {
    const seq = ++editContactTemplateSeq.current;
    if (!contactId) {
      setEditFormData((prev) => ({
        ...prev,
        contactId: '',
        templateId: '',
        templateName: '',
        title: '',
      }));
      return;
    }
    setEditFormData((prev) => ({ ...prev, contactId }));
    try {
      const res = await bookingTemplatesAPI.getTemplateByContactId(contactId);
      if (seq !== editContactTemplateSeq.current) return;
      const tmpl = unwrapTemplateFromByContactResponse(res);
      if (!tmpl) {
        setEditFormData((prev) => ({
          ...prev,
          contactId,
          templateId: '',
          templateName: '',
          title: '',
        }));
        return;
      }
      const name = templateDisplayName(tmpl);
      const tid = tmpl.id ?? tmpl.templateId;
      setEditFormData((prev) => ({
        ...prev,
        contactId,
        templateId: tid != null && tid !== '' ? String(tid) : '',
        templateName: name,
        title: (name || prev.title).slice(0, MAX_SLOT_TITLE_LENGTH),
        description: String(tmpl.description != null ? tmpl.description : prev.description).slice(0, MAX_SLOT_DESCRIPTION_LENGTH),
        price: limitSlotPriceInput(tmpl.price != null ? String(tmpl.price) : prev.price),
      }));
    } catch {
      if (seq !== editContactTemplateSeq.current) return;
      setEditFormData((prev) => ({
        ...prev,
        contactId,
        templateId: '',
        templateName: '',
        title: '',
      }));
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchContacts();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (contactsLoaded) {
      if (!filters.contactId && contacts.length > 0) {
        setFilters(prev => ({ ...prev, contactId: contacts[0].id }));
      } else if (filters.contactId || contacts.length === 0) {
        fetchSlots();
      }
    }
  }, [page, rowsPerPage, filters, contactsLoaded, contacts]);

  // Add slot: keep duration equal to end − start (API validates they match).
  useEffect(() => {
    if (!openAddDialog) return;
    const start = normalizeSlotTime(addFormData.startTime);
    const end = normalizeSlotTime(addFormData.endTime);
    const m = minutesBetween(start, end);
    if (m > 0) {
      setAddFormData((prev) => (prev.duration === m ? prev : { ...prev, duration: m }));
    }
  }, [openAddDialog, addFormData.startTime, addFormData.endTime]);

  // Fetch slots
  const fetchSlots = async () => {
    setLoading(true);
    try {
      const response = await slotsAPI.getSlots(filters, page, rowsPerPage);
      if (response?.data?.success) {
        let slotsData = response.data.data || [];
        slotsData.sort((a, b) =>
          (b.id && a.id) ? String(b.id).localeCompare(String(a.id), undefined, { numeric: true }) : 0
        );
        setTotalCount(response.data.statistics?.totalSlots || slotsData.length);
        setStatistics(response.data.statistics || {
          totalSlots: 0,
          availableSlots: 0,
          bookedSlots: 0,
          todaysSlots: 0
        });

        // Group slots by date
        const grouped = groupSlotsByDate(slotsData);
        setGroupedSlots(grouped);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      showSnackbar('Error fetching slots', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Group slots by date
  const groupSlotsByDate = (slotsData) => {
    const grouped = {};
    slotsData.forEach(slot => {
      const date = slot.date ? (typeof slot.date === 'string' ? slot.date.split('T')[0] : new Date(slot.date).toISOString().split('T')[0]) : 'Unknown';
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });

    // Sort each group by start time ascending
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => {
        const timeA = a.startTime || '00:00:00';
        const timeB = b.startTime || '00:00:00';
        return timeA.localeCompare(timeB);
      });
    });

    return grouped;
  };

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      const response = await contactsAPI.getContacts('', '', 0, 100);
      if (response?.data?.success) {
        setContacts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setContactsLoaded(true);
    }
  };

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const response = await bookingTemplatesAPI.getTemplates({}, 0, 100);
      if (response?.data?.success) {
        setTemplates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Handle generate slots
  const handleGenerateSlots = async () => {
    if (!generateFormData.templateId) {
      showSnackbar('Please select a booking template before generating slots.', 'error');
      return;
    }
    // Validate dates before submission
    if (!validateDates(generateFormData.startDate, generateFormData.endDate)) {
      return;
    }

    setSubmitting(true);
    try {
      const toIsoDate = (d) => (d ? `${d}T00:00:00Z` : null);
      const payload = {
        contactId: Number.parseInt(generateFormData.contactId, 10),
        startDate: toIsoDate(generateFormData.startDate),
        endDate: toIsoDate(generateFormData.endDate),
        startTime: generateFormData.startTime,
        endTime: generateFormData.endTime,
        duration: Number.parseInt(generateFormData.duration, 10),
        daysOfWeek: (generateFormData.daysOfWeek || []).map((d) => d.toUpperCase()),
        templateId: generateFormData.templateId ? Number.parseInt(generateFormData.templateId, 10) : 0,
        templateName: generateFormData.templateName || '',
        title: String(generateFormData.title || '').slice(0, MAX_SLOT_TITLE_LENGTH),
        description: String(generateFormData.description || '').slice(0, MAX_SLOT_DESCRIPTION_LENGTH),
        price: parseSlotPrice(generateFormData.price),
        approvalType: 'AUTO',
        isRecurring: Boolean(generateFormData.isRecurring),
        source: 'system',
        excludeHolidays: !!generateFormData.excludeHolidays,
        maxSlotsPerDay: Number.parseInt(generateFormData.maxSlotsPerDay, 10) || 0
      };

      const response = await slotsAPI.generateSlots(payload);
      if (response?.data?.success) {
        showSnackbar(`Successfully generated ${response.data.data?.length || 0} slots`, 'success');
        setOpenGenerateDialog(false);
        fetchSlots();
        resetGenerateForm();
      }
    } catch (error) {
      console.error('Error generating slots:', error);
      showSnackbar(error.response?.data?.message || 'Error generating slots', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle add slot — body matches POST /api/customer/generate-slots/add-slot (Swagger)
  const handleAddSlot = async () => {
    if (!addFormData.date || addFormData.date < getTodayDate()) {
      showSnackbar('Please select a valid date (today or later)', 'error');
      return;
    }
    if (!addFormData.contactId) {
      showSnackbar('Please select a contact', 'error');
      return;
    }

    const startN = normalizeSlotTime(addFormData.startTime);
    const endN = normalizeSlotTime(addFormData.endTime);
    const durationNum = minutesBetween(startN, endN);
    if (durationNum < 1) {
      toast.error('End time must be after start time.', { position: 'top-center' });
      return;
    }

    if (addFormData.maxCapacity > 1000) {
      showSnackbar(t('pages.slots.validation.maxCapacity'), 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        contactId: Number.parseInt(addFormData.contactId, 10),
        date: addFormData.date,
        startTime: startN,
        endTime: endN,
        duration: durationNum,
        maxCapacity: Number.parseInt(addFormData.maxCapacity, 10) || 1,
        templateId: addFormData.templateId ? Number.parseInt(addFormData.templateId, 10) : 0,
        templateName: addFormData.templateName || '',
        title: String(addFormData.title || '').slice(0, MAX_SLOT_TITLE_LENGTH),
        description: String(addFormData.description || '').slice(0, MAX_SLOT_DESCRIPTION_LENGTH),
        price: parseSlotPrice(addFormData.price),
        approvalType: addFormData.approvalType || 'AUTO'
      };

      const response = await slotsAPI.addSlot(payload);
      const body = response?.data;

      if (body?.success === true) {
        toast.success('Slot added successfully', { position: 'top-center' });
        setOpenAddDialog(false);
        fetchSlots();
        resetAddForm();
      } else {
        const msg = slotsApiErrorMessage(body) || 'Failed to add slot';
        toast.error(msg, { position: 'top-center', autoClose: 8000 });
      }
    } catch (error) {
      console.error('Error adding slot:', error);
      const data = error?.response?.data;
      const msg = slotsApiErrorMessage(data) || error.message || 'Error adding slot';
      toast.error(msg, { position: 'top-center', autoClose: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit slot
  const handleEditSlot = async () => {
    if (!editFormData.date || editFormData.date < getTodayDate()) {
      showSnackbar('Please select a valid date (today or later)', 'error');
      return;
    }
    if (!editFormData.contactId) {
      showSnackbar('Please select a contact', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        contactId: Number.parseInt(editFormData.contactId, 10),
        date: editFormData.date,
        startTime: editFormData.startTime,
        endTime: editFormData.endTime,
        duration: Number.parseInt(editFormData.duration, 10),
        maxCapacity: Number.parseInt(editFormData.maxCapacity, 10) || 1,
        templateId: editFormData.templateId ? Number.parseInt(editFormData.templateId, 10) : 0,
        templateName: editFormData.templateName || '',
        title: String(editFormData.title || '').slice(0, MAX_SLOT_TITLE_LENGTH),
        description: String(editFormData.description || '').slice(0, MAX_SLOT_DESCRIPTION_LENGTH),
        price: parseSlotPrice(editFormData.price),
        approvalType: editFormData.approvalType || 'AUTO'
      };

      const response = await slotsAPI.updateSlot(selectedSlot.id, payload);
      if (response?.data?.success) {
        showSnackbar('Slot updated successfully', 'success');
        setOpenEditDialog(false);
        fetchSlots();
      }
    } catch (error) {
      console.error('Error updating slot:', error);
      showSnackbar(error.response?.data?.message || 'Error updating slot', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete slot
  const handleDeleteSlot = async () => {
    setSubmitting(true);
    try {
      const response = await slotsAPI.deleteSlot(selectedSlot.id);
      if (response?.data?.success) {
        toast.success('Slot deleted successfully', { position: 'top-center' });
        setOpenDeleteDialog(false);
        fetchSlots();
      }
    } catch (error) {
      console.error('Error deleting slot:', error);
      showSnackbar(error.response?.data?.message || 'Error deleting slot', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Open delete dialog
  const openDeleteDialogHandler = (slot) => {
    setSelectedSlot(slot);
    setOpenDeleteDialog(true);
  };

  // Handle template selection for Generate
  const handleTemplateChange = (templateId) => {
    if (templateId === '' || templateId == null) {
      setGenerateFormData((prev) => ({
        ...prev,
        templateId: '',
        templateName: '',
      }));
      return;
    }
    const template = templates.find((t) => String(t.id) === String(templateId));
    if (template) {
      const name = templateOptionLabel(template);
      setGenerateFormData((prev) => ({
        ...prev,
        templateId: String(template.id),
        templateName: name,
        title: name.slice(0, MAX_SLOT_TITLE_LENGTH),
        description: String(template.description || '').slice(0, MAX_SLOT_DESCRIPTION_LENGTH),
        duration: template.duration || 60,
        price: limitSlotPriceInput(template.price == null ? '' : String(template.price)),
        bookingType: normalizeTemplateBookingType(template.bookingType) || 'ONLINE',
      }));
    }
  };

  // Reset generate form
  const resetGenerateForm = () => {
    setGenerateFormData({
      contactId: '',
      startDate: '',
      endDate: '',
      startTime: '09:00',
      endTime: '17:00',
      duration: 60,
      daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      templateId: '',
      templateName: '',
      title: '',
      description: '',
      price: '',
      bookingType: 'ONLINE',
      isRecurring: false,
      recurringGroupId: null,
      source: 'CUSTOM',
      excludeHolidays: true,
      maxSlotsPerDay: 8
    });
  };

  // Reset add form
  const resetAddForm = () => {
    setAddFormData({
      contactId: '',
      date: '',
      startTime: '10:00',
      endTime: '10:40',
      duration: 40,
      maxCapacity: 1,
      templateId: '',
      templateName: '',
      title: '',
      description: '',
      price: '',
      approvalType: 'AUTO'
    });
  };

  // Show snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getSlotContactDisplayName = (slot) => {
    const linkedContact = contacts.find((c) => String(c.id) === String(slot.contactId));
    return (
      slot.contactPerson ||
      slot.contactName ||
      linkedContact?.contactPerson ||
      linkedContact?.title ||
      linkedContact?.name ||
      `ID: ${slot.contactId}`
    );
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      contactId: contacts.length > 0 ? contacts[0].id : '',
      templateId: '',
      startDate: '',
      endDate: '',
      status: '',
      bookingType: ''
    });
    setPage(0);
  };

  // Filter date pickers — same style as Bookings (dd/MM/yyyy, year/month/day views)
  const slotsFilterStartDateValue = useMemo(() => {
    if (!filters.startDate) return null;
    const d = parse(filters.startDate, 'yyyy-MM-dd', new Date());
    return isValid(d) ? d : null;
  }, [filters.startDate]);

  const slotsFilterEndDateValue = useMemo(() => {
    if (!filters.endDate) return null;
    const d = parse(filters.endDate, 'yyyy-MM-dd', new Date());
    return isValid(d) ? d : null;
  }, [filters.endDate]);

  const generateContactTemplates = useMemo(() => {
    const base = templatesForContactSelection(templates, generateFormData.contactId);
    return mergeSelectedTemplateOption(
      base,
      generateFormData.templateId,
      generateFormData.templateName,
      templates
    );
  }, [templates, generateFormData.contactId, generateFormData.templateId, generateFormData.templateName]);

  const addContactTemplates = useMemo(() => {
    const base = templatesForContactSelection(templates, addFormData.contactId);
    return mergeSelectedTemplateOption(base, addFormData.templateId, addFormData.templateName, templates);
  }, [templates, addFormData.contactId, addFormData.templateId, addFormData.templateName]);

  const editContactTemplates = useMemo(() => {
    const base = templatesForContactSelection(templates, editFormData.contactId);
    return mergeSelectedTemplateOption(base, editFormData.templateId, editFormData.templateName, templates);
  }, [templates, editFormData.contactId, editFormData.templateId, editFormData.templateName]);

  const slotsFilterDatePickerSlotProps = {
    textField: {
      fullWidth: true,
      color: 'primary',
      InputLabelProps: { shrink: true, color: 'primary' },
      InputProps: {
        sx: { borderRadius: '12px', bgcolor: 'white' },
      },
    },
    field: { clearable: true },
  };

  const handleSlotsFilterStartDateChange = (newValue) => {
    setPage(0);
    setFilters((prev) => {
      const next = { ...prev };
      if (newValue == null || !isValid(newValue)) {
        next.startDate = '';
        return next;
      }
      next.startDate = format(newValue, 'yyyy-MM-dd');
      if (prev.endDate) {
        const end = parse(prev.endDate, 'yyyy-MM-dd', new Date());
        if (isValid(end) && newValue > end) next.endDate = '';
      }
      return next;
    });
  };

  const handleSlotsFilterEndDateChange = (newValue) => {
    setPage(0);
    setFilters((prev) => {
      if (newValue == null || !isValid(newValue)) {
        return { ...prev, endDate: '' };
      }
      return { ...prev, endDate: format(newValue, 'yyyy-MM-dd') };
    });
  };

  const handleTodayFilterClick = () => {
    const today = getTodayDate();
    setFilters((prev) => ({
      ...prev,
      status: '',
      startDate: today,
      endDate: today,
    }));
    setPage(0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = parse(dateString.split('T')[0], 'yyyy-MM-dd', new Date());
    if (!isValid(d)) return dateString;
    return format(d, 'MMM d, yyyy');
  };

  // Format date for display with day name
  const formatDateWithDay = (dateString) => {
    if (!dateString) return 'N/A';
    const d = parse(dateString.split('T')[0], 'yyyy-MM-dd', new Date());
    if (!isValid(d)) return dateString;
    return format(d, 'EEEE, MMMM d, yyyy');
  };

  // Format time (from "HH:MM:SS.0" to "HH:MM")
  const formatTime = formatSlotTime;

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Validate dates
  const validateDates = (startDate, endDate) => {
    const today = getTodayDate();

    if (startDate < today) {
      showSnackbar('Start date cannot be in the past', 'error');
      return false;
    }

    if (endDate < startDate) {
      showSnackbar('End date cannot be before start date', 'error');
      return false;
    }

    return true;
  };

  const renderGroupedSlotsContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (Object.keys(groupedSlots).length === 0) {
      return (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <EventNote sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('pages.slots.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('pages.slots.empty.subline')}
          </Typography>
        </Paper>
      );
    }

    return (
      <>
        {Object.keys(groupedSlots).sort((dateA, dateB) => dateA.localeCompare(dateB)).map((date) => {
          const slotsForDate = groupedSlots[date];

          return (
            <Accordion
              key={date}
              defaultExpanded={true}
              sx={{
                mb: 3,
                borderRadius: '16px !important',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid #e8eaed',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#5c6bc0' }} />}
                sx={{
                  background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecff 100%)',
                  color: '#3f51b5',
                  px: 3,
                  borderBottom: '1px solid #e8eaed',
                  '& .MuiAccordionSummary-content': { alignItems: 'center' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 1 }}>
                  <CalendarToday sx={{ fontSize: '1.5rem', color: '#5c6bc0' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, flex: 1, letterSpacing: '-0.02em', color: '#3f51b5' }}>
                    {formatDateWithDay(date)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, bgcolor: '#fafbfc' }}>
                <TableContainer sx={{ bgcolor: '#fff' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f7ff' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#5c6bc0', fontSize: '0.8rem' }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#5c6bc0', fontSize: '0.8rem' }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#5c6bc0', fontSize: '0.8rem' }}>Contact</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#5c6bc0', fontSize: '0.8rem' }}>Template</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#5c6bc0', fontSize: '0.8rem' }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#5c6bc0', fontSize: '0.8rem' }}>Price</TableCell>

                        <TableCell sx={{ fontWeight: 600, color: '#5c6bc0', fontSize: '0.8rem' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#5c6bc0', fontSize: '0.8rem' }} align="center">{t('pages.slots.table.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {slotsForDate.map((slot) => (
                        <SlotTableRow
                          key={slot.id}
                          slot={slot}
                          contactDisplayName={getSlotContactDisplayName(slot)}
                          onDelete={openDeleteDialogHandler}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}

        <Paper elevation={0} sx={{ mt: 3, p: 1, borderRadius: '12px', border: '1px solid #e8eaed', bgcolor: '#fafbfc' }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[50, 100, 150, 200, 500, 1000, 2000]}
            labelRowsPerPage={t('pages.slots.rowsPerPage')}
            labelDisplayedRows={formatTablePaginationLabel}
          />
        </Paper>
      </>
    );
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, pr: { xs: 1, sm: 2 } }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
          p: { xs: 2, sm: 4 },
          borderRadius: { xs: 2, sm: 4 },
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: '1px solid rgba(255, 140, 66, 0.2)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: '#313132' }}>
            {t('pages.slots.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
            {t('pages.slots.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            fullWidth={isMobile}
            startIcon={<Refresh />}
            onClick={fetchSlots}
            disabled={loading}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              height: '48px',
              px: 3
            }}
          >
            {t('pages.slots.refresh')}
          </Button>
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<Add />}
            onClick={() => setOpenAddDialog(true)}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              background: '#FF8C42',
              boxShadow: '0 4px 15px rgba(255, 140, 66, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: '#E06526',
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: '0 8px 25px rgba(255, 140, 66, 0.4)',
              },
              '&:active': { transform: 'translateY(0)' }
            }}
          >
            {t('pages.slots.addSlot')}
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Paper
            elevation={0}
            onClick={() => handleFilterChange('status', '')}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              bgcolor: '#f0f4ff',
              border: '1px solid',
              borderColor: filters.status === '' ? '#3f51b5' : 'rgba(63, 81, 181, 0.15)',
              boxShadow: filters.status === '' ? '0 8px 20px rgba(63, 81, 181, 0.12)' : '0 2px 8px rgba(63, 81, 181, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              height: "95%",
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(63, 81, 181, 0.12)' },
            }}
          >
            <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#3f51b5' }}>{statistics.totalSlots}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#5c6bc0', display: 'block', lineHeight: 1.2 }}>{t('pages.slots.stats.total')}</Typography>
            <EventNote sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#3f51b5' }} />
          </Paper>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Paper
            elevation={0}
            onClick={() => handleFilterChange('status', 'AVAILABLE')}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              bgcolor: '#e8f5e9',
              border: '1px solid',
              borderColor: filters.status === 'AVAILABLE' ? '#2e7d32' : 'rgba(76, 175, 80, 0.2)',
              boxShadow: filters.status === 'AVAILABLE' ? '0 8px 20px rgba(46, 125, 50, 0.12)' : '0 2px 8px rgba(76, 175, 80, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              height: "95%",
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(46, 125, 50, 0.12)' },
            }}
          >
            <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#2e7d32' }}>{statistics.availableSlots}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#43a047', display: 'block', lineHeight: 1.2 }}>{t('pages.slots.stats.available')}</Typography>
            <CheckCircle sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#2e7d32' }} />
          </Paper>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Paper
            elevation={0}
            onClick={() => handleFilterChange('status', 'FULL')}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              bgcolor: '#ffebee',
              border: '1px solid',
              borderColor: filters.status === 'FULL' ? '#c62828' : 'rgba(244, 67, 54, 0.2)',
              boxShadow: filters.status === 'FULL' ? '0 8px 20px rgba(198, 40, 40, 0.12)' : '0 2px 8px rgba(244, 67, 54, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              height: "95%",
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(198, 40, 40, 0.12)' },
            }}
          >
            <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#c62828' }}>
              {statistics.bookedSlots || 0}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#e53935', display: 'block', lineHeight: 1.2 }}>{t('pages.slots.stats.booked')}</Typography>
            <CalendarToday sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#c62828' }} />
          </Paper>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Paper
            elevation={0}
            onClick={handleTodayFilterClick}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              bgcolor: '#e3f2fd',
              border: '1px solid',
              borderColor: filters.startDate === getTodayDate() && filters.endDate === getTodayDate() ? '#0277bd' : 'rgba(33, 150, 243, 0.2)',
              boxShadow: filters.startDate === getTodayDate() && filters.endDate === getTodayDate() ? '0 8px 20px rgba(3, 155, 229, 0.12)' : '0 2px 8px rgba(33, 150, 243, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              height: "95%",
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(3, 155, 229, 0.12)' },
            }}
          >
            <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#0277bd' }}>{statistics.todaysSlots || 0}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#039be5', display: 'block', lineHeight: 1.2 }}>{t('pages.slots.stats.today')}</Typography>
            <AttachMoney sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#0277bd' }} />
          </Paper>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}>
            <InputLabel>{t('pages.slots.filter.contact')}</InputLabel>
            <Select
              value={filters.contactId}
              label={t('pages.slots.filter.contact')}
              onChange={(e) => handleFilterChange('contactId', e.target.value)}
            >

              {contacts.map((contact) => (
                <MenuItem key={contact.id} value={contact.id}>{contact.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            format="dd/MM/yyyy"
            label={t('pages.slots.filter.startDate')}
            value={slotsFilterStartDateValue}
            onChange={handleSlotsFilterStartDateChange}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={slotsFilterDatePickerSlotProps}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            format="dd/MM/yyyy"
            label={t('pages.slots.filter.endDate')}
            value={slotsFilterEndDateValue}
            onChange={handleSlotsFilterEndDateChange}
            views={['year', 'month', 'day']}
            openTo="day"
            minDate={slotsFilterStartDateValue || undefined}
            slotProps={slotsFilterDatePickerSlotProps}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Close />}
            onClick={clearFilters}
            sx={{
              height: '56px',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              borderWidth: '2px',
              '&:hover': { borderWidth: '2px' }
            }}
          >
            {t('pages.slots.clearFilters')}
          </Button>
        </Grid>
      </Grid>

      {/* Slots Grouped by Date */}
      <Box sx={{ mb: 3 }}>
        {renderGroupedSlotsContent()}
      </Box>

      {/* Generate Slots Dialog */}
      <Dialog
        open={openGenerateDialog}
        onClose={() => !submitting && setOpenGenerateDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '20px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ background: '#ffffff', color: '#000000', px: 3, py: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('pages.slots.generate.title')}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Quickly create availability for a range of dates</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Contact</InputLabel>
                <Select
                  value={generateFormData.contactId}
                  label="Contact"
                  onChange={(e) => applyContactTemplateToGenerateForm(e.target.value)}
                >
                  {contacts.map((contact) => (
                    <MenuItem key={contact.id} value={contact.id}>
                      {contact.title}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('pages.slots.form.contactLoadsTemplateTitle')}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!generateFormData.contactId}>
                <InputLabel required>Booking Template</InputLabel>
                <Select
                  required
                  value={generateFormData.templateId === '' || generateFormData.templateId == null ? '' : String(generateFormData.templateId)}
                  label="Booking Template"
                  onChange={(e) => handleTemplateChange(e.target.value)}
                >
                  <MenuItem value="" disabled>Select a booking template</MenuItem>
                  {generateContactTemplates.map((template) => (
                    <MenuItem key={`gen-tpl-${template.id}`} value={String(template.id)}>
                      {templateOptionLabel(template)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Start Date"
                value={generateFormData.startDate}
                onChange={(e) => setGenerateFormData(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: getTodayDate() }}
                helperText="Cannot select past dates"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="End Date"
                value={generateFormData.endDate}
                onChange={(e) => setGenerateFormData(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: generateFormData.startDate || getTodayDate() }}
                helperText="Must be on or after start date"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="time"
                label="Start Time"
                value={generateFormData.startTime}
                onChange={(e) => setGenerateFormData(prev => ({ ...prev, startTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="time"
                label="End Time"
                value={generateFormData.endTime}
                onChange={(e) => setGenerateFormData(prev => ({ ...prev, endTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="number"
                label="Duration (minutes)"
                value={generateFormData.duration}
                onChange={(e) => setGenerateFormData(prev => ({ ...prev, duration: e.target.value }))}
                InputProps={{ inputProps: { min: 15, max: 480 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Days of Week
              </Typography>
              <FormGroup row>
                {daysOfWeek.map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={generateFormData.daysOfWeek.includes(day)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setGenerateFormData(prev => ({
                              ...prev,
                              daysOfWeek: [...prev.daysOfWeek, day]
                            }));
                          } else {
                            setGenerateFormData(prev => ({
                              ...prev,
                              daysOfWeek: prev.daysOfWeek.filter(d => d !== day)
                            }));
                          }
                        }}
                      />
                    }
                    label={day}
                  />
                ))}
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Title"
                value={generateFormData.title}
                onChange={(e) => setGenerateFormData(prev => ({ ...prev, title: e.target.value.slice(0, MAX_SLOT_TITLE_LENGTH) }))}
                inputProps={{ maxLength: MAX_SLOT_TITLE_LENGTH }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={generateFormData.description}
                onChange={(e) => setGenerateFormData(prev => ({ ...prev, description: e.target.value.slice(0, MAX_SLOT_DESCRIPTION_LENGTH) }))}
                inputProps={{ maxLength: MAX_SLOT_DESCRIPTION_LENGTH }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Price"
                value={generateFormData.price}
                onChange={(e) => setGenerateFormData(prev => ({ ...prev, price: limitSlotPriceInput(e.target.value) }))}
                InputProps={{
                  inputProps: { min: 0, max: MAX_SLOT_PRICE, step: 0.01 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Booking Type</InputLabel>
                <Select
                  value={generateFormData.bookingType}
                  label="Booking Type"
                  onChange={(e) => setGenerateFormData(prev => ({ ...prev, bookingType: e.target.value }))}
                >
                  {bookingTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Max Slots Per Day"
                value={generateFormData.maxSlotsPerDay}
                onChange={(e) => setGenerateFormData(prev => ({ ...prev, maxSlotsPerDay: e.target.value }))}
                InputProps={{ inputProps: { min: 1, max: 50 } }}
              />
            </Grid>


            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={generateFormData.excludeHolidays}
                    onChange={(e) => setGenerateFormData(prev => ({
                      ...prev,
                      excludeHolidays: e.target.checked
                    }))}
                  />
                }
                label="Exclude Holidays"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={generateFormData.isRecurring}
                    onChange={(e) => setGenerateFormData(prev => ({
                      ...prev,
                      isRecurring: e.target.checked
                    }))}
                  />
                }
                label="Is Recurring"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #eee', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-end', gap: 1.5 }}>
          <Button onClick={() => setOpenGenerateDialog(false)} disabled={submitting} sx={{ borderRadius: '10px', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateSlots}
            variant="contained"
            disabled={submitting || !generateFormData.contactId || !generateFormData.startDate ||
              !generateFormData.endDate || !generateFormData.title ||
              generateFormData.daysOfWeek.length === 0}
            sx={{
              borderRadius: '10px',
              fontWeight: 700,
              px: 4,
              background: 'linear-gradient(135deg, #1a237e 0%, #303f9f 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #283593 0%, #3f51b5 100%)' }
            }}
          >
            {submitting ? <CircularProgress size={24} /> : t('pages.slots.generate.now')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Slot Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => !submitting && setOpenAddDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '20px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ background: '#ffffff', color: '#000000', px: 3, py: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('pages.slots.addSlot')}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>{t('pages.slots.filter.contact')}</InputLabel>
                <Select
                  value={addFormData.contactId}
                  label={t('pages.slots.filter.contact')}
                  onChange={(e) => applyContactTemplateToAddForm(e.target.value)}
                >
                  {contacts.map((contact) => (
                    <MenuItem key={contact.id} value={contact.id}>
                      {contact.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="date"
                label={t('pages.slots.filter.startDate')}
                value={addFormData.date}
                onChange={(e) => setAddFormData(prev => ({ ...prev, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: getTodayDate() }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="time"
                label={t('pages.slots.form.startTime')}
                value={addFormData.startTime}
                onChange={(e) => setAddFormData(prev => ({ ...prev, startTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="time"
                label={t('pages.slots.form.endTime')}
                value={addFormData.endTime}
                onChange={(e) => setAddFormData(prev => ({ ...prev, endTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="number"
                label={t('pages.slots.form.durationMin')}
                value={addFormData.duration}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!addFormData.contactId}>
                <InputLabel>{t('pages.slots.form.templateOptional2')}</InputLabel>
                <Select
                  value={addFormData.templateId === '' || addFormData.templateId == null ? '' : String(addFormData.templateId)}
                  label={t('pages.slots.form.templateOptional2')}
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) {
                      setAddFormData((prev) => ({
                        ...prev,
                        templateId: '',
                        templateName: '',
                      }));
                      return;
                    }
                    const tmpl = templates.find((t) => String(t.id) === String(id));
                    const label = tmpl ? templateOptionLabel(tmpl) : '';
                    setAddFormData((prev) => ({
                      ...prev,
                      templateId: String(id),
                      templateName: label,
                      title: (label || prev.title).slice(0, MAX_SLOT_TITLE_LENGTH),
                      description: String(tmpl?.description ?? prev.description).slice(0, MAX_SLOT_DESCRIPTION_LENGTH),
                      price: limitSlotPriceInput(tmpl?.price != null ? String(tmpl.price) : prev.price),
                    }));
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  {addContactTemplates.map((tmpl) => (
                    <MenuItem key={`add-tpl-${tmpl.id}`} value={String(tmpl.id)}>
                      {templateOptionLabel(tmpl)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('pages.slots.form.maxCapacity')}
                value={addFormData.maxCapacity}
                onChange={(e) => setAddFormData(prev => ({ ...prev, maxCapacity: e.target.value }))}
                InputProps={{ inputProps: { min: 1, max: 1000 } }}
                error={addFormData.maxCapacity > 1000}
                helperText={addFormData.maxCapacity > 1000 ? t('pages.slots.validation.maxCapacity') : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('pages.slots.form.slotTitleLabel')}
                value={addFormData.title}
                onChange={(e) => setAddFormData(prev => ({ ...prev, title: e.target.value.slice(0, MAX_SLOT_TITLE_LENGTH) }))}
                inputProps={{ maxLength: MAX_SLOT_TITLE_LENGTH }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('pages.slots.form.description')}
                multiline
                minRows={2}
                value={addFormData.description}
                onChange={(e) => setAddFormData(prev => ({ ...prev, description: e.target.value.slice(0, MAX_SLOT_DESCRIPTION_LENGTH) }))}
                inputProps={{ maxLength: MAX_SLOT_DESCRIPTION_LENGTH }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('pages.slots.form.price')}
                value={addFormData.price}
                onChange={(e) => setAddFormData(prev => ({ ...prev, price: limitSlotPriceInput(e.target.value) }))}
                InputProps={{ inputProps: { min: 0, max: MAX_SLOT_PRICE, step: 0.01 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #eee', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-end', gap: 1.5 }}>
          <Button onClick={() => setOpenAddDialog(false)} disabled={submitting} sx={{ borderRadius: '10px', fontWeight: 600 }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddSlot}
            variant="contained"
            disabled={submitting || !addFormData.contactId || !addFormData.date || !addFormData.startTime || !addFormData.endTime || !addFormData.duration}
            sx={{
              borderRadius: '10px',
              fontWeight: 700,
              px: 4,
              background: '#FF8C42',
              '&:hover': { background: '#E06526' }
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : t('pages.slots.btn.createSlot')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Slot Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => !submitting && setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ background: '#ffffff', color: '#000000', px: 3, py: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Edit Availability Slot</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Contact</InputLabel>
                <Select
                  value={editFormData.contactId}
                  label="Contact"
                  onChange={(e) => applyContactTemplateToEditForm(e.target.value)}
                >
                  {contacts.map((contact) => (
                    <MenuItem key={contact.id} value={contact.id}>
                      {contact.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="date"
                label="Date"
                value={editFormData.date}
                onChange={(e) => setEditFormData(prev => ({ ...prev, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: getTodayDate() }}
                helperText="Cannot select past dates"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="time"
                label="Start Time"
                value={editFormData.startTime}
                onChange={(e) => setEditFormData(prev => ({ ...prev, startTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="time"
                label="End Time"
                value={editFormData.endTime}
                onChange={(e) => setEditFormData(prev => ({ ...prev, endTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="number"
                label="Duration (minutes)"
                value={editFormData.duration}
                onChange={(e) => setEditFormData(prev => ({ ...prev, duration: e.target.value }))}
                InputProps={{ inputProps: { min: 15, max: 480 } }}
                helperText="e.g. 40"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!editFormData.contactId}>
                <InputLabel>Template (optional)</InputLabel>
                <Select
                  value={editFormData.templateId === '' || editFormData.templateId == null ? '' : String(editFormData.templateId)}
                  label="Template (optional)"
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) {
                      setEditFormData((prev) => ({
                        ...prev,
                        templateId: '',
                        templateName: '',
                      }));
                      return;
                    }
                    const tmpl = templates.find((t) => String(t.id) === String(id));
                    const label = tmpl ? templateOptionLabel(tmpl) : '';
                    setEditFormData((prev) => ({
                      ...prev,
                      templateId: String(id),
                      templateName: label,
                      title: (label || prev.title).slice(0, MAX_SLOT_TITLE_LENGTH),
                      description: String(tmpl?.description ?? prev.description).slice(0, MAX_SLOT_DESCRIPTION_LENGTH),
                      price: limitSlotPriceInput(tmpl?.price != null ? String(tmpl.price) : prev.price),
                    }));
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  {editContactTemplates.map((tmpl) => (
                    <MenuItem key={`edit-tpl-${tmpl.id}`} value={String(tmpl.id)}>
                      {templateOptionLabel(tmpl)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Capacity"
                value={editFormData.maxCapacity}
                onChange={(e) => setEditFormData(prev => ({ ...prev, maxCapacity: e.target.value }))}
                InputProps={{ inputProps: { min: 1, max: 1000 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Slot Title"
                value={editFormData.title}
                onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value.slice(0, MAX_SLOT_TITLE_LENGTH) }))}
                inputProps={{ maxLength: MAX_SLOT_TITLE_LENGTH }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                minRows={2}
                value={editFormData.description}
                onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value.slice(0, MAX_SLOT_DESCRIPTION_LENGTH) }))}
                inputProps={{ maxLength: MAX_SLOT_DESCRIPTION_LENGTH }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Price"
                value={editFormData.price}
                onChange={(e) => setEditFormData(prev => ({ ...prev, price: limitSlotPriceInput(e.target.value) }))}
                InputProps={{ inputProps: { min: 0, max: MAX_SLOT_PRICE, step: 0.01 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #eee' }}>
          <Button onClick={() => setOpenEditDialog(false)} disabled={submitting} sx={{ borderRadius: '10px', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSlot}
            variant="contained"
            disabled={submitting || !editFormData.contactId || !editFormData.date || !editFormData.startTime || !editFormData.endTime || !editFormData.duration}
            sx={{
              borderRadius: '10px',
              fontWeight: 700,
              px: 4,
              background: '#FF8C42',
              '&:hover': { background: '#E06526' }
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : t('pages.slots.btn.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !submitting && setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Slot</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this slot? This action cannot be undone.
          </Typography>
          {selectedSlot && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Title:</strong> {selectedSlot.title}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate(selectedSlot.date)}
              </Typography>
              <Typography variant="body2">
                <strong>Time:</strong> {formatTime(selectedSlot.startTime)} - {formatTime(selectedSlot.endTime)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSlot}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Slots;
